// src/utils/displayableWebsiteUrl.js
//
// CommonJS, pure-function — shared by every frontend surface that renders
// b_website as a clickable link (candidate list modal, public profile pages,
// company profile, sharing previews, etc.).
//
// Problem this solves:
//   Phase 5 (GBP signup) canonicalizes the pasted Google Maps URL via
//   `canonicalizeGbpUrl()` (src/server/services/google/gbpVerification.js)
//   to fit varchar(255). Canonicalization strips the @lat,lng,zoom view
//   state, the !data block with the FTID/CID, and the entire query string —
//   leaving only `https://www.google.com/maps/place/<biz-name>/`. That
//   short form CANNOT resolve on Google Maps when clicked — Maps does a
//   search-by-name and falls back to centering on the viewer's IP location
//   when ambiguous. Visitors land on a blank map nowhere near the business.
//
//   Lekkey's salon was the first GBP signup to hit this in production
//   (2026-05-25). Her b_website rendered as a broken link on every public
//   surface.
//
// Fix:
//   When b_website matches the canonicalized-GBP pattern AND b_place_id
//   is available (it IS — same row, same SELECT * call), construct
//   Google's documented "Place ID URL" form. Maps resolves these
//   directly to the listing, no search-fallback nonsense.
//
//   Spec: https://developers.google.com/maps/documentation/urls/get-started#place-action
//   Form: https://www.google.com/maps/place/?q=place_id:<PLACE_ID>
//
// Non-goals (intentionally leaves these unchanged):
//   - g.page/<slug> URLs — work fine when clicked, no transform needed
//   - business.google.com/<slug> URLs — same, work fine
//   - Real domain URLs (mybiz.com) — obviously
//   - Any URL without b_place_id available — falls through
//
// When to revisit:
//   - If canonicalizeGbpUrl is ever changed to preserve enough URL state
//     to resolve directly (would require a longer column or a different
//     storage strategy), this utility becomes a no-op for new rows but
//     still useful for old rows. Safe to keep.
//   - If Google ever changes the Place ID URL format, update the
//     CANONICALIZED_GBP_RE template here in one place.

// Matches the OUTPUT shape of canonicalizeGbpUrl() in gbpVerification.js:
// protocol + maybe-www + google.com + /maps/place/ + name + maybe-trailing-slash.
// No @coords, no !data, no query string — that's the whole pattern of the bug.
const CANONICALIZED_GBP_RE = /^https?:\/\/(www\.)?google\.com\/maps\/place\/[^/?#]+\/?$/i;

/**
 * Resolve the best displayable URL for a business's b_website field.
 * Returns a string safe to drop into <a href="..."> with target="_blank".
 *
 * @param {string|null|undefined} bWebsite - The stored b_website value
 * @param {string|null|undefined} bPlaceId - The stored b_place_id (used to
 *   construct a guaranteed-working Maps URL when b_website is the bare
 *   canonicalized GBP form)
 * @returns {string|null} The URL to use in href, or null if no usable URL
 */
function resolveDisplayableWebsiteUrl(bWebsite, bPlaceId) {
  const raw = (bWebsite == null ? '' : String(bWebsite)).trim();
  if (!raw) return null;

  // The canonicalized-GBP rescue path. Only applies when the stored value
  // matches the exact "search by name" shape AND we have a place_id to
  // construct a proper deep link with.
  if (bPlaceId && CANONICALIZED_GBP_RE.test(raw)) {
    return `https://www.google.com/maps/place/?q=place_id:${encodeURIComponent(bPlaceId)}`;
  }

  // For everything else, normalize protocol the same way the existing
  // call sites do: assume https:// if missing, otherwise pass through.
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw.replace(/^\/+|\/+$/g, '')}`;
}

module.exports = {
  resolveDisplayableWebsiteUrl,
  // Exported for tests + the rare caller who wants to know WHY a URL got
  // transformed (e.g., to show a "this is your Google Business Profile"
  // hint next to the link in the UI).
  CANONICALIZED_GBP_RE,
};
